import React from 'react';
import { useState, useEffect, useRef, memo } from 'react';
import { ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Button from '../ui/Button';

// Custom hook for counter animation
const useCounter = (end: number, duration: number = 1500, shouldStart: boolean = false) => {
  const [count, setCount] = useState(0);
  
  useEffect(() => {
    if (!shouldStart) return;
    
    let startTime: number;
    const animate = (currentTime: number) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      const easeOut = 1 - Math.pow(1 - progress, 2);
      setCount(Math.floor(easeOut * end));
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    requestAnimationFrame(animate);
  }, [end, duration, shouldStart]);
  
  return count;
};

// Custom hook for text switching animation
const useTextSwitcher = (texts: string[], interval: number = 3000) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % texts.length);
    }, interval);
    
    return () => clearInterval(timer);
  }, [texts.length, interval]);
  
  return texts[currentIndex];
};

const Hero: React.FC = memo(() => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const statsRef = useRef<HTMLDivElement>(null);

  const agentCount = useCounter(1000, 1500, isVisible);
  const premiumCount = useCounter(40, 1500, isVisible);
  const switchingText = useTextSwitcher(['company growth.', 'insurance career.'], 3000);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3, rootMargin: '50px' }
    );
    
    if (statsRef.current) {
      observer.observe(statsRef.current);
    }
    
    return () => observer.disconnect();
  }, []);
  
  const handleApplyClick = React.useCallback(() => {
    navigate('/apply');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [navigate]);

  const handleInternalStoreClick = React.useCallback(() => {
    navigate('/agent/login');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [navigate]);

  return (
    <section className="relative min-h-screen py-16 sm:py-20 lg:py-32 overflow-hidden bg-gradient-to-br from-slate-50 via-white to-slate-100">
      {/* Stock Chart Line Background */}
      <div className="absolute inset-0 z-0 opacity-20">
        <svg 
          className="absolute inset-0 w-full h-full pointer-events-none"
          viewBox="0 0 1400 900"
          preserveAspectRatio="none"
        >
          <defs>
            <linearGradient id="stockGradient" x1="0%" y1="100%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="rgba(21, 60, 124, 0.4)" />
              <stop offset="50%" stopColor="rgba(21, 60, 124, 0.5)" />
              <stop offset="100%" stopColor="rgba(21, 60, 124, 0.6)" />
            </linearGradient>
            <filter id="glow">
              <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
              <feMerge>
                <feMergeNode in="coloredBlur"/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
          </defs>

          <path
            d="M -200 750
               Q -140 730, -80 700
               Q -20 680, 40 720
               Q 100 760, 160 680
               Q 220 620, 280 660
               Q 340 700, 400 620
               Q 460 560, 520 600
               Q 580 640, 640 520
               Q 700 420, 760 460
               Q 820 500, 880 380
               Q 940 280, 1000 320
               Q 1060 360, 1120 220
               Q 1180 120, 1240 100
               Q 1300 80, 1400 60"
            stroke="rgba(21, 60, 124, 1)"
            strokeWidth="4"
            fill="none"
            filter="url(#glow)"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{
              strokeDasharray: '3200',
              strokeDashoffset: '3200',
              animation: 'drawLine 12s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards'
            }}
          />
          
          <path
            d="M -200 750
               Q -140 730, -80 700
               Q -20 680, 40 720
               Q 100 760, 160 680
               Q 220 620, 280 660
               Q 340 700, 400 620
               Q 460 560, 520 600
               Q 580 640, 640 520
               Q 700 420, 760 460
               Q 820 500, 880 380
               Q 940 280, 1000 320
               Q 1060 360, 1120 220
               Q 1180 120, 1240 100
               Q 1300 80, 1400 60
               L 1400 900
               L -200 900 Z"
            fill="url(#stockGradient)"
            opacity="0.15"
            style={{
              animation: 'fillChart 2s ease-out forwards 10s'
            }}
          />
        </svg>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-10 items-center mt-4 sm:mt-6">
          {/* Content */}
          <div className="opacity-0 animate-[fadeInUp_0.6s_ease-out_0.1s_forwards]">
            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black leading-tight mb-4 sm:mb-6 will-change-transform">
              <span className="text-navy-900 block opacity-0 animate-[fadeInUp_0.6s_ease-out_0.2s_forwards]">
                Enhance your
              </span>
              <span className="text-navy-700 block opacity-0 animate-[fadeInUp_0.6s_ease-out_0.4s_forwards] transition-all duration-300">
                {switchingText}
              </span>
            </h1>
            <p className="text-sm sm:text-lg mb-6 sm:mb-8 text-slate-600 opacity-0 animate-[fadeInUp_0.6s_ease-out_0.6s_forwards] max-w-lg leading-relaxed">
              Best training in the game. Top carrier access. Leads that actually convert.
              Whether you're an agent looking to grow or an agency looking to expand. We are here to make sure that happens.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-6 sm:mb-8 opacity-0 animate-[fadeInUp_0.6s_ease-out_0.8s_forwards]">
              <Button
                variant="primary"
                size="lg"
                onClick={handleApplyClick}
                className="inline-flex items-center justify-center gap-2 !px-5 !py-2.5 sm:!px-8 sm:!py-3.5 text-sm sm:text-base font-semibold"
              >
                <span>Apply Now</span>
                <ArrowRight className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="lg"
                onClick={handleInternalStoreClick}
                className="inline-flex items-center justify-center gap-2 !px-5 !py-2.5 sm:!px-8 sm:!py-3.5 text-sm sm:text-base font-semibold"
              >
                <span>Agent Login</span>
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
            
            {/* Stats */}
            <div 
              ref={statsRef}
              className="flex justify-center lg:justify-start gap-2 sm:gap-4 lg:gap-5 pt-5 sm:pt-6 border-t border-slate-200 opacity-0 animate-[fadeInUp_0.6s_ease-out_1.0s_forwards]"
            >
              <div className="bg-white/70 backdrop-blur-xl border border-slate-200/60 rounded-xl px-3 sm:px-5 lg:px-6 py-2 sm:py-3 lg:py-4 shadow-lg shadow-slate-200/50 text-center flex-1 max-w-[90px] sm:max-w-[110px] lg:max-w-[130px]">
                <div className="text-base sm:text-2xl lg:text-3xl font-bold text-navy-700">
                  {agentCount}+
                </div>
                <div className="text-[8px] sm:text-[10px] lg:text-xs text-slate-500 mt-0.5">Agents</div>
              </div>
              <div className="bg-white/70 backdrop-blur-xl border border-slate-200/60 rounded-xl px-3 sm:px-5 lg:px-6 py-2 sm:py-3 lg:py-4 shadow-lg shadow-slate-200/50 text-center flex-1 max-w-[90px] sm:max-w-[110px] lg:max-w-[130px]">
                <div className="text-base sm:text-2xl lg:text-3xl font-bold text-navy-700">2022</div>
                <div className="text-[8px] sm:text-[10px] lg:text-xs text-slate-500 mt-0.5">Founded</div>
              </div>
              <div className="bg-white/70 backdrop-blur-xl border border-slate-200/60 rounded-xl px-3 sm:px-5 lg:px-6 py-2 sm:py-3 lg:py-4 shadow-lg shadow-slate-200/50 text-center flex-1 max-w-[90px] sm:max-w-[110px] lg:max-w-[130px]">
                <div className="text-base sm:text-2xl lg:text-3xl font-bold text-navy-700">
                  ${premiumCount}M+
                </div>
                <div className="text-[8px] sm:text-[10px] lg:text-xs text-slate-500 mt-0.5">Premium</div>
              </div>
            </div>
          </div>

          {/* Right Side - What We Offer with EC imprint */}
          <div className="relative bg-white border border-slate-200 rounded-xl sm:rounded-2xl px-5 sm:px-8 py-4 sm:py-6 shadow-lg opacity-0 animate-[fadeInScale_0.6s_ease-out_0.8s_forwards] overflow-hidden">
            {/* Grid Pattern Background */}
            <div className="absolute inset-0 bg-[linear-gradient(rgba(148,163,184,0.08)_1px,transparent_1px),linear-gradient(90deg,rgba(148,163,184,0.08)_1px,transparent_1px)] [background-size:20px_20px] pointer-events-none"></div>
            {/* EC Imprint */}
            <div className="absolute -right-4 sm:-right-8 top-1/2 -translate-y-1/2 pointer-events-none select-none">
              <span className="text-[100px] sm:text-[180px] font-black text-slate-100/80 tracking-tighter leading-none">EC</span>
            </div>
            <h2 className="text-base sm:text-lg font-semibold text-slate-900 mb-3 sm:mb-5 relative z-10">What you get with us:</h2>
            <div className="space-y-2.5 sm:space-y-3 relative z-10">
              <div className="flex items-center gap-2 sm:gap-3">
                <span className="text-navy-600 font-bold text-sm sm:text-base">/</span>
                <div>
                  <span className="font-medium text-slate-900 text-sm sm:text-base">Day 1 residuals</span>
                  <span className="text-xs sm:text-sm text-slate-500 ml-2">Start earning from your first sale</span>
                </div>
              </div>
              <div className="flex items-center gap-2 sm:gap-3">
                <span className="text-navy-600 font-bold text-sm sm:text-base">/</span>
                <div>
                  <span className="font-medium text-slate-900 text-sm sm:text-base">In-house lead generation</span>
                  <span className="text-xs sm:text-sm text-slate-500 ml-2">Fresh leads, aged leads, dialer seats</span>
                </div>
              </div>
              <div className="flex items-center gap-2 sm:gap-3">
                <span className="text-navy-600 font-bold text-sm sm:text-base">/</span>
                <div>
                  <span className="font-medium text-slate-900 text-sm sm:text-base">28+ carrier access</span>
                  <span className="text-xs sm:text-sm text-slate-500 ml-2">More options = more closed deals</span>
                </div>
              </div>
              <div className="flex items-center gap-2 sm:gap-3">
                <span className="text-navy-600 font-bold text-sm sm:text-base">/</span>
                <div>
                  <span className="font-medium text-slate-900 text-sm sm:text-base">CRM + dialer included</span>
                  <span className="text-xs sm:text-sm text-slate-500 ml-2">Closio CRM and Readymode access</span>
                </div>
              </div>
              <div className="flex items-center gap-2 sm:gap-3">
                <span className="text-navy-600 font-bold text-sm sm:text-base">/</span>
                <div>
                  <span className="font-medium text-slate-900 text-sm sm:text-base">Real support</span>
                  <span className="text-xs sm:text-sm text-slate-500 ml-2">Discord + direct access to leadership</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
});

Hero.displayName = 'Hero';

export default Hero;
